import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { addFeatureToRelease } from "../api/featuresApi";
import "./AddFeatureModal.css";

/**
 * AddFeatureModal
 * Calls API 3: POST /api/releases/{id}/features
 *
 * Release type is always CLIENT (no selector shown).
 *
 * @param {boolean}  show
 * @param {function} onHide
 * @param {object}   release   - { releaseId, releaseName }
 * @param {function} onSuccess - called with success message string after add
 */
const AddFeatureModal = ({ show, onHide, release, onSuccess }) => {
    const [featureId, setFeatureId] = useState("");
    const [featureName, setFeatureName] = useState("");
    const [version, setVersion] = useState("1.0.0");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // Reset form every time modal opens
    useEffect(() => {
        if (show) {
            setFeatureId("");
            setFeatureName("");
            setVersion("1.0.0");
            setSubmitting(false);
            setError("");
        }
    }, [show]);

    /* ── Auto-derive Feature ID from Feature Name ─────────────── */
    const deriveId = (name) =>
        "FEATURE_" +
        name.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");

    const handleNameChange = (e) => {
        const name = e.target.value;
        setFeatureName(name);
        // Auto-update ID only while user hasn't manually edited it
        if (!featureId || featureId === deriveId(featureName)) {
            setFeatureId(deriveId(name));
        }
    };

    const handleIdChange = (e) => {
        setFeatureId(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""));
    };

    /* ── Submit ───────────────────────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        const id = featureId.trim().toUpperCase();
        const name = featureName.trim();
        const ver = version.trim() || "1.0.0";

        if (!name) return setError("Feature Name is required.");
        if (!id) return setError("Feature ID is required.");
        if (!/^[A-Z0-9_]+$/.test(id))
            return setError("Feature ID must only contain uppercase letters, numbers, and underscores.");

        setSubmitting(true);
        setError("");

        try {
            if (!release?.releaseId) {
                setError("No release selected. Please close this modal and try again.");
                setSubmitting(false);
                return;
            }
            const result = await addFeatureToRelease(release.releaseId, {
                featureId: id,
                featureName: name,
                version: ver,
                releaseType: "CLIENT",
            });

            onSuccess?.(`Feature "${name}" (${id}) added successfully.`, result?.data);
            onHide();
        } catch (err) {
            setError(err?.message || "Failed to add feature. Please try again.");
            setSubmitting(false);
        }
    };

    const canSubmit = featureId.trim().length > 0 && featureName.trim().length > 0 && !submitting;

    /* ── Render ───────────────────────────────────────────────── */
    return (
        <Modal
            show={show}
            onHide={onHide}
            className="add-feature-modal"
            centered
            size="md"
        >
            {/* ── Header ──────────────────────────────────── */}
            <div className="afm-header">
                <div className="afm-header__icon-wrap">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                </div>
                <div className="afm-header__text">
                    <h5 className="afm-header__title">Add Feature</h5>
                    <p className="afm-header__sub">
                        to <strong>{release?.releaseName || "this release"}</strong>
                    </p>
                </div>
                <button
                    type="button"
                    className="afm-header__close"
                    onClick={() => !submitting && onHide()}
                    disabled={submitting}
                    aria-disabled={submitting}
                    aria-label="Close"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            {/* ── Body ────────────────────────────────────── */}
            <Form onSubmit={handleSubmit} noValidate>
                <div className="afm-body">
                    {error && (
                        <div className="afm-error" role="alert">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            {error}
                        </div>
                    )}

                    {/* Feature Name */}
                    <div className="afm-field">
                        <label className="afm-field__label" htmlFor="afm-feature-name">
                            Feature Name <span className="afm-field__required">*</span>
                        </label>
                        <input
                            id="afm-feature-name"
                            type="text"
                            className="afm-field__input"
                            placeholder="e.g. Dark Mode"
                            value={featureName}
                            onChange={handleNameChange}
                            maxLength={128}
                            required
                            autoFocus
                        />
                        <p className="afm-field__hint">
                            Human-readable name. The Feature ID is auto-generated from this.
                        </p>
                    </div>

                    {/* Feature ID + Version — side by side */}
                    <div className="afm-row">
                        <div className="afm-field afm-field--grow">
                            <label className="afm-field__label" htmlFor="afm-feature-id">
                                Feature ID <span className="afm-field__required">*</span>
                                <span className="afm-field__badge">auto</span>
                            </label>
                            <input
                                id="afm-feature-id"
                                type="text"
                                className="afm-field__input afm-field__input--mono"
                                placeholder="FEATURE_DARK_MODE"
                                value={featureId}
                                onChange={handleIdChange}
                                maxLength={64}
                                required
                            />
                        </div>
                        <div className="afm-field afm-field--version">
                            <label className="afm-field__label" htmlFor="afm-version">
                                Version
                            </label>
                            <input
                                id="afm-version"
                                type="text"
                                className="afm-field__input"
                                placeholder="1.0.0"
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                maxLength={32}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Footer ──────────────────────────────────── */}
                <div className="afm-footer">
                    <button type="button" className="afm-footer__cancel" onClick={onHide} disabled={submitting}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="afm-footer__submit"
                        disabled={!canSubmit}
                    >
                        {submitting ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"
                                    aria-hidden="true"
                                    className="afm-spinner">
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                Adding…
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                                Add Feature
                            </>
                        )}
                    </button>
                </div>
            </Form>
        </Modal>
    );
};

export default AddFeatureModal;
